import { P2GBox, P2GCheckBoxLegacy, P2GGrid, P2GLink, P2GTextField, P2GText } from '@parcel2go/core-ui';
import { ConsignmentParcelItemApiModel, Dimensions, PackageApiModel } from '~/lib/api/smartsend/types';
import { NumberedList } from '../../NumberedList';
import { DimensionsSelection } from '../DimensionsSelection';
import { GroupingParcelProps } from './GroupingParcelProps';
import { AppCard } from '~/components/card/app-card';
import { toDisplayCurrency } from '~/components/currency/toDisplayCurrency';
import { TranslationNamespaces } from '~/lib/constants';
import { useTranslation } from 'react-i18next';

interface props extends GroupingParcelProps {
  onParcelDimensionsUpdate?: (parcelUID: string, dims: Dimensions) => void;
  hideDimensionsIfNotRequired?: boolean;
  getPackages?: () => Promise<PackageApiModel[]>;
  children?: React.ReactNode;
  currencyCode: string;
}

export const GroupingPanelParcels: React.FC<props> = (props) => {
  const { t } = useTranslation(TranslationNamespaces.SMARTSEND);
  function renderDescControl(o: ConsignmentParcelItemApiModel) {
    if (!props.updateDescription) {
      return (
        <P2GTextField
          fullWidth
          id="Desc"
          label={t('ITEM_DESCRIPTION_INPUT_LABEL', { defaultValue: 'Item Description' })}
          value={o.Description}
          disabled
          onChange={() => {}}
        />
      );
    }

    return (
      <P2GTextField
        fullWidth
        label={t('ITEM_DESCRIPTION_INPUT_LABEL', { defaultValue: 'Item Description' })}
        id="Desc"
        value={o.Description}
        onChange={(v) => props.updateDescription?.(o.UID, v.currentTarget.value)}
      />
    );
  }

  return (
    <NumberedList
      items={props.parcels.map((p, pi) => (
        <AppCard key={p.UID}>
          <P2GBox mt={pi > 0 ? 2 : 0} p={2}>
            {p.Orders.map((o) => (
              <P2GBox key={o.UID} pb={1}>
                <P2GGrid container wrap="nowrap" alignContent="center" alignItems="center" gap={1}>
                  {props.onSelect && (
                    <P2GGrid item flexShrink={1}>
                      <P2GCheckBoxLegacy
                        onChange={() => props.onSelect?.(o.UID)}
                        checked={props.selected?.includes(o.UID)}
                        labelPlacement="end"
                        variant="primary"
                        error={false}
                        label={null}
                        id="parcel"
                      />
                    </P2GGrid>
                  )}
                  <P2GGrid item flexShrink={1}>
                    <P2GText variant="body2">{o.Quantity} x</P2GText>
                  </P2GGrid>
                  <P2GGrid item flexGrow={1}>
                    {renderDescControl(o)}
                  </P2GGrid>
                  {props.allowInlineDimensions && o.Quantity > 1 && o.Quantity <= 10 && (
                    <P2GGrid item flexShrink={1}>
                      <P2GLink onClick={() => props.expand?.(o)}>
                        {t('EXPAND_LINK', { defaultValue: 'Expand' })}
                      </P2GLink>
                    </P2GGrid>
                  )}
                  <P2GGrid item flexShrink={1}>
                    <P2GText variant="body2">{toDisplayCurrency(props.currencyCode, o.Value)}</P2GText>
                  </P2GGrid>
                </P2GGrid>
              </P2GBox>
            ))}
            {p.Orders.find((o) => props.selected?.includes(o.UID)) && props.children}
            {props.allowInlineDimensions && (
              <DimensionsSelection
                startMinimised={true}
                getPackages={props.getPackages}
                dimensions={p.Dimensions}
                onUpdate={(d) => props.onParcelDimensionsUpdate?.(p.UID, d)}
                dimensionsRequired={props.dimensionsRequired}
                hideDimensionsIfNotRequired={props.hideDimensionsIfNotRequired}
              />
            )}
          </P2GBox>
        </AppCard>
      ))}
    />
  );
};
